/** Pre-built option structures, built off the replayed chain.
 *
 * A template is a SHAPE, not a set of strikes: each leg names an offset in
 * STRIKES FROM ATM, and the strikes themselves come from the chain's own
 * listed ladder. That is the same rule the ATM already follows — nearest
 * LISTED strike, never a rounded one — and it earns its keep twice over:
 * NIFTY's 50-point ladder and SENSEX's 100-point one need no per-index
 * config, and a template can never name a strike the archive does not
 * hold.
 *
 * ⚠️ BUILDING IS ALLOWED TO FAIL, LOUDLY. Sessions before 2026-08-08 were
 * captured narrow — a handful of strikes, often only one side of each — so
 * a four-legged structure frequently cannot be built at all. Returning a
 * partial basket would be the worst outcome: a three-legged "iron condor"
 * prices and charts perfectly happily while being a different trade. Every
 * builder therefore returns EITHER every leg or a reason, never a subset.
 *
 * ⚠️ THE EXOTIC NAMES ARE OURS. StockMock does not publish the leg
 * construction behind "Batman" or "Double Plateau", so those two carry the
 * standard textbook shape and the UI shows every card's legs before you
 * commit them. Where a name is ambiguous the legs on screen are the
 * authority, not the label.
 */

import type { SimChainRow, SimLeg } from "./simulatorApi";

export type Side = "CE" | "PE";
export type Direction = "BUY" | "SELL";

/** Which shelf a structure sits on. Mirrors the simulator this replaces:
 * a reader picks by market view first, structure second. */
export type Bias = "Neutral" | "Bullish" | "Bearish";

/** One leg of a template, positioned relative to ATM.
 *
 * `offset` counts LISTED strikes, signed the way the ladder runs: +1 is one
 * strike above ATM, -1 one below. It is NOT a point distance, and NOT a
 * multiple of the width — `width` is applied by the catalogue when it
 * builds the template, so a leg here is already resolved.
 */
export interface TemplateLeg {
  side: Side;
  direction: Direction;
  offset: number;
  /** Ratio legs carry 2; everything else 1. */
  lots: number;
}

export interface StrategyTemplate {
  key: string;
  name: string;
  bias: Bias;
  /** One line on what the structure is FOR — the view it expresses, not a
   * restatement of its legs, which the card already lists. */
  blurb: string;
  /** Legs for a given width, in strikes. Width only matters to structures
   * with an OTM wing; a straddle ignores it. */
  legs: (width: number) => TemplateLeg[];
}

/** Widths the panel offers. 1 is deliberately included: on a narrow
 * archived session it is often the only width that can be built at all. */
export const WIDTHS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_WIDTH = 2;

const leg = (side: Side, direction: Direction, offset: number, lots = 1): TemplateLeg => ({
  side,
  direction,
  offset,
  lots,
});

/** The catalogue.
 *
 * Sign convention throughout: calls sit ABOVE ATM (+w) and puts BELOW
 * (-w), so every "wing" offset reads as its real position on the ladder
 * rather than as an unsigned distance the reader has to re-interpret per
 * side.
 */
export const TEMPLATES: readonly StrategyTemplate[] = [
  {
    key: "short-straddle",
    name: "Short Straddle",
    bias: "Neutral",
    blurb: "Collect both premiums and expect the market to sit still. Risk is unbounded.",
    legs: () => [leg("CE", "SELL", 0), leg("PE", "SELL", 0)],
  },
  {
    key: "long-straddle",
    name: "Long Straddle",
    bias: "Neutral",
    blurb: "Pay for movement in either direction. Loses if nothing happens.",
    legs: () => [leg("CE", "BUY", 0), leg("PE", "BUY", 0)],
  },
  {
    key: "short-strangle",
    name: "Short Strangle",
    bias: "Neutral",
    blurb: "A wider short straddle: less premium, a wider band to be right in.",
    legs: (w) => [leg("CE", "SELL", w), leg("PE", "SELL", -w)],
  },
  {
    key: "long-strangle",
    name: "Long Strangle",
    bias: "Neutral",
    blurb: "Cheaper than a straddle, and needs a bigger move to pay.",
    legs: (w) => [leg("CE", "BUY", w), leg("PE", "BUY", -w)],
  },
  {
    key: "short-iron-condor",
    name: "Short Iron Condor",
    bias: "Neutral",
    blurb: "A short strangle with bought wings — the same view, with the tail risk capped.",
    legs: (w) => [
      leg("PE", "BUY", -2 * w),
      leg("PE", "SELL", -w),
      leg("CE", "SELL", w),
      leg("CE", "BUY", 2 * w),
    ],
  },
  {
    key: "long-iron-condor",
    name: "Long Iron Condor",
    bias: "Neutral",
    blurb: "The condor reversed: pays at the wings, loses in the middle. Defined both ways.",
    legs: (w) => [
      leg("PE", "SELL", -2 * w),
      leg("PE", "BUY", -w),
      leg("CE", "BUY", w),
      leg("CE", "SELL", 2 * w),
    ],
  },
  {
    key: "short-iron-butterfly",
    name: "Short Iron Butterfly",
    bias: "Neutral",
    blurb: "A short straddle with wings. Maximum credit, and it all rests on ATM.",
    legs: (w) => [
      leg("PE", "BUY", -w),
      leg("PE", "SELL", 0),
      leg("CE", "SELL", 0),
      leg("CE", "BUY", w),
    ],
  },
  {
    key: "long-iron-butterfly",
    name: "Long Iron Butterfly",
    bias: "Neutral",
    blurb: "Pays if price leaves the wings, capped on both sides.",
    legs: (w) => [
      leg("PE", "SELL", -w),
      leg("PE", "BUY", 0),
      leg("CE", "BUY", 0),
      leg("CE", "SELL", w),
    ],
  },
  {
    key: "double-plateau",
    name: "Double Plateau",
    bias: "Neutral",
    blurb: "Two capped shelves either side of ATM — a wider condor with the middle sold twice.",
    legs: (w) => [
      leg("PE", "BUY", -2 * w),
      leg("PE", "SELL", -w),
      leg("PE", "SELL", 0),
      leg("CE", "SELL", 0),
      leg("CE", "SELL", w),
      leg("CE", "BUY", 2 * w),
    ],
  },
  {
    key: "jade-lizard",
    name: "Jade Lizard",
    bias: "Neutral",
    blurb: "Short put plus a short call spread. Sized so there is no risk to the upside.",
    legs: (w) => [leg("PE", "SELL", -w), leg("CE", "SELL", w), leg("CE", "BUY", 2 * w)],
  },
  {
    key: "reverse-jade-lizard",
    name: "Reverse Jade Lizard",
    bias: "Neutral",
    blurb: "The mirror: short call plus a short put spread, with no risk to the downside.",
    legs: (w) => [leg("CE", "SELL", w), leg("PE", "SELL", -w), leg("PE", "BUY", -2 * w)],
  },
  {
    key: "call-ratio-spread",
    name: "Call Ratio Spread",
    bias: "Neutral",
    blurb: "Buy one call, sell two further out. Mildly bullish, unbounded above.",
    legs: (w) => [leg("CE", "BUY", 0), leg("CE", "SELL", w, 2)],
  },
  {
    key: "put-ratio-spread",
    name: "Put Ratio Spread",
    bias: "Neutral",
    blurb: "Buy one put, sell two further out. Mildly bearish, unbounded below.",
    legs: (w) => [leg("PE", "BUY", 0), leg("PE", "SELL", -w, 2)],
  },
  {
    key: "batman",
    name: "Batman",
    bias: "Neutral",
    blurb: "Two ratio spreads, one each side — twin peaks away from ATM, a dip over it.",
    legs: (w) => [
      leg("PE", "BUY", -w),
      leg("PE", "SELL", -2 * w, 2),
      leg("CE", "BUY", w),
      leg("CE", "SELL", 2 * w, 2),
    ],
  },
  {
    key: "short-put",
    name: "Short Put",
    bias: "Bullish",
    blurb: "Collect premium and be willing to be long if it comes to you. Unbounded below.",
    legs: () => [leg("PE", "SELL", 0)],
  },
  {
    key: "long-call",
    name: "Long Call",
    bias: "Bullish",
    blurb: "The whole view in one leg. Loses the premium and no more.",
    legs: () => [leg("CE", "BUY", 0)],
  },
  {
    key: "bull-call-spread",
    name: "Bull Call Spread",
    bias: "Bullish",
    blurb: "Long a call, short one above it. Defined cost, defined reward.",
    legs: (w) => [leg("CE", "BUY", 0), leg("CE", "SELL", w)],
  },
  {
    key: "bull-put-spread",
    name: "Bull Put Spread",
    bias: "Bullish",
    blurb: "Sell a put spread below the market and keep the credit if it holds.",
    legs: (w) => [leg("PE", "SELL", -w), leg("PE", "BUY", -2 * w)],
  },
  {
    key: "bull-condor",
    name: "Bull Condor",
    bias: "Bullish",
    blurb: "A condor whose profit shelf sits ABOVE the money rather than over it.",
    legs: (w) => [
      leg("CE", "BUY", 0),
      leg("CE", "SELL", w),
      leg("CE", "SELL", 2 * w),
      leg("CE", "BUY", 3 * w),
    ],
  },
  {
    key: "bull-butterfly",
    name: "Bull Butterfly",
    bias: "Bullish",
    blurb: "A butterfly pinned one wing above the money — pays if it drifts up and stops.",
    legs: (w) => [leg("CE", "BUY", 0), leg("CE", "SELL", w, 2), leg("CE", "BUY", 2 * w)],
  },
  {
    key: "call-ratio-back-spread",
    name: "Call Ratio Back Spread",
    bias: "Bullish",
    blurb: "Sell one call, buy two above it. Small loss on a drift, unbounded on a run.",
    legs: (w) => [leg("CE", "SELL", 0), leg("CE", "BUY", w, 2)],
  },
  {
    key: "range-forward",
    name: "Range Forward",
    bias: "Bullish",
    blurb: "Long call funded by a short put — synthetic long, with a gap in the middle.",
    legs: (w) => [leg("CE", "BUY", w), leg("PE", "SELL", -w)],
  },
  {
    key: "short-call",
    name: "Short Call",
    bias: "Bearish",
    blurb: "Collect premium against a rally that does not come. Unbounded above.",
    legs: () => [leg("CE", "SELL", 0)],
  },
  {
    key: "long-put",
    name: "Long Put",
    bias: "Bearish",
    blurb: "The whole view in one leg. Loses the premium and no more.",
    legs: () => [leg("PE", "BUY", 0)],
  },
  {
    key: "bear-call-spread",
    name: "Bear Call Spread",
    bias: "Bearish",
    blurb: "Sell a call spread above the market and keep the credit if it holds.",
    legs: (w) => [leg("CE", "SELL", w), leg("CE", "BUY", 2 * w)],
  },
  {
    key: "bear-put-spread",
    name: "Bear Put Spread",
    bias: "Bearish",
    blurb: "Long a put, short one below it. Defined cost, defined reward.",
    legs: (w) => [leg("PE", "BUY", 0), leg("PE", "SELL", -w)],
  },
  {
    key: "bear-condor",
    name: "Bear Condor",
    bias: "Bearish",
    blurb: "A condor whose profit shelf sits BELOW the money rather than over it.",
    legs: (w) => [
      leg("PE", "BUY", 0),
      leg("PE", "SELL", -w),
      leg("PE", "SELL", -2 * w),
      leg("PE", "BUY", -3 * w),
    ],
  },
  {
    key: "bear-butterfly",
    name: "Bear Butterfly",
    bias: "Bearish",
    blurb: "A butterfly pinned one wing below the money — pays if it drifts down and stops.",
    legs: (w) => [leg("PE", "BUY", 0), leg("PE", "SELL", -w, 2), leg("PE", "BUY", -2 * w)],
  },
  {
    key: "put-ratio-back-spread",
    name: "Put Ratio Back Spread",
    bias: "Bearish",
    blurb: "Sell one put, buy two below it. Small loss on a drift, huge on a break.",
    legs: (w) => [leg("PE", "SELL", 0), leg("PE", "BUY", -w, 2)],
  },
  {
    key: "risk-reversal",
    name: "Risk Reversal",
    bias: "Bearish",
    blurb: "Long put funded by a short call — synthetic short, with a gap in the middle.",
    legs: (w) => [leg("PE", "BUY", -w), leg("CE", "SELL", w)],
  },
];

/** A template resolved against a real chain: either every leg, or why not. */
export type BuildResult = { ok: true; legs: SimLeg[] } | { ok: false; reason: string };

function quoteAt(row: SimChainRow, side: Side): number | null {
  const q = side === "CE" ? row.call : row.put;
  // A zero premium is a real price on a far-OTM leg; only absence disqualifies.
  return q ? q.ltp : null;
}

/** Resolve a template against the chain as it stood at `at`.
 *
 * `rows` is expected ascending by strike, as the chain endpoint returns it.
 * The ATM row's INDEX is the origin, so offsets step along listed strikes
 * and skip nothing — a gap in the ladder shifts every outer leg inward by
 * exactly the strikes that are missing, which is the honest reading of "two
 * strikes out" on a chain that only lists those strikes.
 */
export function buildTemplate(
  template: StrategyTemplate,
  rows: SimChainRow[],
  atm: number | null,
  width: number,
  at: string,
): BuildResult {
  if (atm === null) return { ok: false, reason: "no ATM strike for this minute" };
  const centre = rows.findIndex((r) => r.strike === atm);
  if (centre === -1) return { ok: false, reason: "ATM strike is not in the chain" };

  const legs: SimLeg[] = [];
  for (const l of template.legs(width)) {
    const row = rows[centre + l.offset];
    if (!row) return { ok: false, reason: `needs ${strikesOut(l.offset)}, not archived` };
    const entry = quoteAt(row, l.side);
    if (entry === null) {
      return { ok: false, reason: `no ${l.side} archived at ${String(row.strike)}` };
    }
    legs.push({
      strike: row.strike,
      side: l.side,
      direction: l.direction,
      lots: l.lots,
      entry,
      entry_at: at,
    });
  }
  return { ok: true, legs };
}

function strikesOut(offset: number): string {
  const n = Math.abs(offset);
  const dir = offset > 0 ? "above" : "below";
  return `${String(n)} strike${n === 1 ? "" : "s"} ${dir} ATM`;
}

/** The bias shelves, in the order the panel shows them. */
export const BIASES: readonly Bias[] = ["Neutral", "Bullish", "Bearish"];
